<!--
  ProducerJoinModalStakes.vue
  Unified A/B variant D ("Stakes") covering all four flows in one component:
    - context = 'general'   → "what producers made last month" hero
    - context = 'auditions' → current progress bar for the audition's star goal
    - context = 'pitch'     → 4-step lifecycle ladder (casting / pitch submission)
    - sfwTrigger=true + logged-in user → auto-routes to age verification

  handleJoin() redirects straight to route('www.join.signUp') — no trial-specific
  URL or vendor_option_id; the guest picks their own plan on the real join page.
  Reuses the same analytics calls, route('members2.auth.login') sign-in link,
  and STAR_ICON_URL as the modals it superseded.

  TODOs are flagged only where data genuinely doesn't exist in the originals
  — namely the "Last month producers made N scenes" stat, the scene-grid
  thumbnails, and the pitch lifecycle copy. Wire those to real sources when
  available.
-->

<script setup>
import {ref, computed, watch, onMounted, onUnmounted, nextTick} from 'vue';
import { Check, Crown, Flame, ShieldCheck, X, Clapperboard } from 'lucide-vue-next';
import {Link, usePage} from '@inertiajs/vue3';
import { trackEvent } from '@/Helpers/analytics';
import { trackAuditionAnalyticsEvent } from '@/Helpers/auditions';
import { useJoinOptions } from '@/composables/useJoinOptions';
import axios from 'axios';

// Reused asset from the other join modals
const STAR_ICON_URL = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png';

const props = defineProps({
    isOpen: { type: Boolean, required: true },
    // Extended to support 'pitch' (casting/pitch-submission flow) alongside
    // the original 'general'/'auditions' values.
    context: {
        type: String,
        default: 'general',
        validator: (v) => ['general', 'auditions', 'pitch'].includes(v),
    },
    audition:  { type: Object,  default: null },
    performer: { type: Object,  default: null },
    source:    { type: String,  default: 'Producer_Join_Modal' },
    // When true, the modal is the SFW gate. Logged-in users see the
    // age-verify branch; guests see the standard join.
    sfwTrigger:   { type: Boolean, default: false },
    ageVerifyUrl: { type: String,  default: '' },
});

const emit = defineEmits(['close']);

const { clearJoinOptions } = useJoinOptions();
const page = usePage();

// ── Auth / branch resolution ────────────────────────────────────────────────
const isLoggedIn = ref(page.props.auth && page.props.auth.user && page.props.auth.user.id !== null)

// When the SFW gate fires for a logged-in user we promote to age-verify.
// Otherwise the explicit context wins.
const effectiveContext = computed(() => {
    if (props.sfwTrigger && isLoggedIn.value) return 'age-verify';
    return props.context;
});

const isAuditionsContext = computed(() => effectiveContext.value === 'auditions');
const isPitchContext     = computed(() => effectiveContext.value === 'pitch');
const isGeneralContext   = computed(() => effectiveContext.value === 'general');
const isAgeVerifyContext = computed(() => effectiveContext.value === 'age-verify');

// ── State ───────────────────────────────────────────────────────────────────
const email = ref('');
const emailError = ref('');
const isVisible = ref(false);

watch(
    () => props.isOpen,
    (open) => {
        isVisible.value = open;
        if (!open) {
            email.value = '';
            emailError.value = '';
        }
    },
    { immediate: true },
);

// ── Audition progress maths ────────────────────────────────────────────────
const progressPercentage = computed(() => {
    if (!props.audition) return 0;
    const p = (props.audition.likes / props.audition.star_goal) * 100;
    return Math.min(Math.round(p), 100);
});
const starsNeeded = computed(() => {
    if (!props.audition) return 0;
    return props.audition.star_goal - props.audition.likes;
});

// ── Pitch lifecycle steps ───────────────────────────────────────────────────
const PITCH_STEPS = [
    { label: 'You pitch',       desc: 'Describe your scene idea' },
    { label: 'Community votes', desc: 'Fans upvote their favorite pitches' },
    { label: 'Casting opens',   desc: 'Top pitches advance into auditions and casting calls' },
    { label: 'NA films it',     desc: 'Your scene, made real' },
];

// ── General "last month" hero ──────────────────────────────────────────────
const releasedAuditionsCount = ref(0);
const releasedAuditionsPosts = ref([]);
const displayedPosts = ref([]);
const isLoadingStats = ref(true);

// Fetch released auditions stats in background on component mount
onMounted(async () => {
    try {
        const response = await axios.get(route(page.props.routePrefix + '.reels.released.stats'));
        if (response.data) {
            releasedAuditionsCount.value = response.data.count || 0;
            releasedAuditionsPosts.value = response.data.posts || [];
        }
    } catch (error) {
        console.error('Failed to fetch released auditions stats:', error);
    } finally {
        isLoadingStats.value = false;
    }
});

// Helper function to populate displayed posts
const populateDisplayedPosts = () => {
    if (releasedAuditionsPosts.value.length > 0) {
        const shuffled = [...releasedAuditionsPosts.value].sort(() => Math.random() - 0.5);
        const selected = shuffled.slice(0, 12);
        displayedPosts.value = [...selected, ...selected]; // Duplicate for seamless loop
    }
};

// Watch for data to load and populate posts if modal is already open
watch(
    () => releasedAuditionsPosts.value,
    (posts) => {
        if (posts.length > 0 && props.isOpen && isGeneralContext.value && displayedPosts.value.length === 0) {
            populateDisplayedPosts();
        }
    }
);

// Shuffle and pick random posts when modal opens, duplicate for infinite scroll
watch(
    () => props.isOpen,
    (open) => {
        if (open && isGeneralContext.value && releasedAuditionsPosts.value.length > 0) {
            populateDisplayedPosts();
        }
    }
);

// ── Theming per branch ──────────────────────────────────────────────────────
const themes = {
    auditions:    { accent: '#3B82F6', glow: 'rgba(59,130,246,0.22)',
        ctaClass: 'bg-blue-600 hover:bg-blue-500 border-blue-500 text-white shadow-blue-600/25',
        linkClass: 'text-blue-400 hover:text-blue-300' },
    pitch:        { accent: '#0052CC', glow: 'rgba(59,130,246,0.22)',
        ctaClass: 'bg-blue-600 hover:bg-blue-500 border-blue-500 text-white shadow-blue-600/25',
        linkClass: 'text-blue-400 hover:text-blue-300' },
    general:      { accent: '#3B82F6', glow: 'rgba(59,130,246,0.22)',
        ctaClass: 'bg-blue-600 hover:bg-blue-500 border-blue-500 text-white shadow-blue-600/25',
        linkClass: 'text-blue-400 hover:text-blue-300' },
    'age-verify': { accent: '#F59E0B', glow: 'rgba(245,158,11,0.22)',
        ctaClass: 'bg-amber-500 hover:bg-amber-400 border-amber-400 text-zinc-950 shadow-amber-500/25',
        linkClass: 'text-amber-400 hover:text-amber-300' },
};
const theme = computed(() => themes[effectiveContext.value] || themes.general);

// ── Copy per branch ─────────────────────────────────────────────────────────
const copy = computed(() => {
    if (isAgeVerifyContext.value) {
        return {
            eyebrow:  'One more step',
            ctaLabel: 'Verify My Age',
        };
    }
    if (isAuditionsContext.value) {
        return {
            eyebrow:  'Your move',
            ctaLabel: 'Sign Up To Sponsor',
            microBullets: ['Monthly Stars with membership', 'Cancel anytime'],
        };
    }
    if (isPitchContext.value) {
        const subject = props.performer?.name;
        return {
            eyebrow:  'How a pitch becomes a scene',
            ctaLabel: subject
                ? `Sign Up & Pitch ${subject}`
                : 'Sign Up & Submit Pitch',
            microBullets: ['Producer account', 'Producer credit if filmed', 'Top pitches enter casting'],
        };
    }
    return {
        eyebrow:  'What producers make',
        ctaLabel: 'Sign Up To Get Started',
        microBullets: ['Monthly Stars with membership', 'Pitch, vote, produce'],
    };
});

// ── Sign-in URL (matches the other modals) ─────────────────────────────────
const currentUrl = computed(() => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.pathname + window.location.search;
    }
    return '';
});

// ── Handlers ────────────────────────────────────────────────────────────────
const handleClose = () => {
    if (props.sfwTrigger) {
        return;
    }

    emit('close');
    clearJoinOptions();
};

// Logged-in SFW path
const handleVerifyAge = () => {
    if (!props.ageVerifyUrl) return;
    trackEvent('Age Verify CTA Clicked', { source: props.source });
    window.location.href = props.ageVerifyUrl;
};

// Guest / general join — vendor_option_id, custom8, signup_origin_url
// cookie, analytics.
const handleJoin = () => {
    const joinType = 'trial';
    const trimmedEmail = email.value.trim();
    emailError.value = '';

    let joinUrl = route('www.join.signUp', { custom8: props.source });

    if (trimmedEmail) {
        joinUrl += `&email=${encodeURIComponent(trimmedEmail)}`;
    }

    console.log(`++ ProducerJoinModalStakes: From ${props.source} Redirecting to`, joinUrl);

    localStorage.setItem('blue_join_options_source', props.source);
    localStorage.setItem('blue_join_options_join_type', joinType);
    localStorage.setItem('blue_join_options_url', joinUrl);

    if (props.audition) {
        // LTIAnalytics is a global already used elsewhere for this cookie.
        LTIAnalytics.setCookie('gold_upgrade_source', props.source);
        LTIAnalytics.setCookie('gold_upgrade_video_id', props.audition.id ?? '');
        trackAuditionAnalyticsEvent('Biller Join Button Clicked', props.audition, {
            store_in_local_storage: true,
            local_storage_key: 'blue_join_options_video_props',
            append_data: { source: props.source, join_type: joinType },
        });
    } else {
        trackEvent('Biller Join Button Clicked', {
            source: props.source,
            join_type: joinType,
        });
    }

    // signup_origin_url cookie — pitch context only, since that's the flow
    // that depends on post-login redirect.
    if (isPitchContext.value) {
        const hostname = window.location.hostname;
        const domainPart = hostname.includes('naughtyamerica.com')
            ? '; domain=.naughtyamerica.com'
            : '';
        document.cookie = `signup_origin_url=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainPart}`;
        const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `signup_origin_url=${encodeURIComponent(currentUrl.value)}; expires=${expires}; path=/${domainPart}; SameSite=Lax`;
    }

    window.location.href = joinUrl;
};
</script>

<template>
    <Teleport to="body">
        <Transition name="stakes-modal">
            <div
                v-if="isOpen"
                class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
                @click.self="handleClose"
            >
                <!-- Backdrop -->
                <div
                    class="absolute inset-0 bg-black/80 backdrop-blur-sm"
                    aria-hidden="true"
                    @click="handleClose"
                />

                <!-- Modal -->
                <div
                    class="relative w-full max-w-[480px] my-auto transform overflow-hidden rounded-2xl sm:rounded-3xl bg-zinc-950 border border-zinc-800 shadow-2xl transition-all duration-300 ease-out max-h-[92vh] overflow-y-auto"
                    :class="isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="stakes-modal-title"
                >
                    <!-- Top theme glow -->
                    <div
                        class="absolute top-0 left-0 right-0 h-48 pointer-events-none opacity-60"
                        :style="{ background: `radial-gradient(ellipse 70% 100% at 50% 0%, ${theme.glow}, transparent 70%)` }"
                    />

                    <!-- Close — hidden on forced SFW flow -->
                    <button
                        v-if="!sfwTrigger"
                        type="button"
                        class="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-600"
                        aria-label="Close"
                        @click="handleClose"
                    >
                        <X class="w-4 h-4" />
                    </button>

                    <div class="relative p-4 sm:p-6 md:p-8">
                        <!-- =================================================== -->
                        <!-- SCENARIO A — Age verification (logged in + SFW gate) -->
                        <!-- =================================================== -->
                        <template v-if="isAgeVerifyContext">
                            <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-center mb-5 text-amber-400">
                                {{ copy.eyebrow }}
                            </p>

                            <div class="flex justify-center mb-6">
                                <div class="relative">
                                    <div class="absolute inset-0 bg-amber-500/20 blur-xl rounded-full" />
                                    <div class="relative flex items-center justify-center w-20 h-20 bg-gradient-to-b from-zinc-800 to-zinc-950 rounded-2xl border border-zinc-700 shadow-inner">
                                        <ShieldCheck class="w-9 h-9 text-amber-400" />
                                    </div>
                                    <div class="absolute -top-1 left-1/2 -translate-x-1/2 bg-amber-500 text-zinc-950 text-[10px] font-bold px-2 py-0.5 rounded-full border border-zinc-900 whitespace-nowrap tracking-wider">
                                        REQUIRED
                                    </div>
                                </div>
                            </div>

                            <div class="text-center mb-6">
                                <h2 id="stakes-modal-title" class="text-[22px] sm:text-2xl font-bold text-white mb-2.5 tracking-tight leading-tight">
                                    Age Verification Required
                                </h2>
                                <p class="text-zinc-400 text-sm leading-relaxed">
                                    Your location requires a quick one-time age verification before
                                    you can access content. It only takes a moment.
                                </p>
                            </div>

                            <!-- Optional audition context strip -->
                            <div
                                v-if="audition"
                                class="bg-zinc-900/50 rounded-xl p-3 sm:p-4 mb-5 border border-zinc-800/50 flex items-center gap-3"
                            >
                                <div class="relative w-12 h-12 rounded-lg overflow-hidden ring-2 ring-zinc-700 shrink-0">
                                    <img
                                        :src="audition.creator?.avatar"
                                        :alt="audition.creator?.name"
                                        class="w-full h-full object-cover"
                                    />
                                </div>
                                <div class="min-w-0">
                                    <p class="text-sm font-semibold text-white">
                                        {{ audition.creator?.name }}
                                    </p>
                                    <p class="text-xs text-zinc-400 line-clamp-1">
                                        Verify to continue watching
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                class="group relative overflow-hidden w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 px-6 font-bold text-base transition-all active:scale-[0.98] shadow-lg border focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-zinc-950"
                                :class="theme.ctaClass"
                                @click="handleVerifyAge"
                            >
                                <ShieldCheck class="w-4 h-4" />
                                <span>{{ copy.ctaLabel }}</span>
                            </button>
                        </template>

                        <!-- ============================================ -->
                        <!-- SCENARIOS B/C/D — Stakes-style join          -->
                        <!-- ============================================ -->
                        <template v-else>
                            <!-- Eyebrow -->
                            <p
                                class="text-[10px] font-bold uppercase tracking-[0.18em] mb-4"
                                :style="{ color: theme.accent }"
                            >{{ copy.eyebrow }}</p>

                            <!-- Headline (per context) -->
                            <h2
                                id="stakes-modal-title"
                                class="text-[22px] sm:text-[26px] font-bold text-white mb-5 tracking-tight leading-[1.15]"
                            >
                                <!-- AUDITION -->
                                <template v-if="isAuditionsContext && audition">
                                    <span class="text-blue-300">{{ audition.likes.toLocaleString() }}</span> stars and counting.
                                </template>
                                <template v-else-if="isAuditionsContext">
                                    Help film the next scene
                                </template>

                                <!-- PITCH -->
                                <template v-else-if="isPitchContext && performer">
                                    Pitch a scene with
                                    <span class="text-blue-300">{{ performer.name }}</span>
                                </template>
                                <template v-else-if="isPitchContext">
                                    From your idea
                                    <span class="text-blue-300">to a real NA scene.</span>
                                </template>

                                <!-- GENERAL -->
                                <template v-else>
                                    The community decides
                                    <span class="text-blue-300">what gets filmed next.</span>
                                </template>
                            </h2>

                            <!-- ─── AUDITION HERO ─────────────────────────────────────── -->
                            <div v-if="isAuditionsContext && audition" class="mb-5">
                                <!-- Creator strip -->
                                <div class="flex items-center gap-3 mb-4">
                                    <img
                                        :src="audition.creator?.avatar"
                                        :alt="audition.creator?.name"
                                        class="w-12 h-12 rounded-xl object-cover object-top border-2 border-zinc-800 shrink-0"
                                    />
                                    <div class="min-w-0">
                                        <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 mb-0.5">
                                            Now casting
                                        </p>
                                        <p class="text-white font-bold text-[15px] truncate leading-tight">
                                            {{ audition.creator?.name }}
                                        </p>
                                        <p class="text-zinc-500 text-xs truncate">{{ audition.title }}</p>
                                    </div>
                                </div>

                                <!-- Big stake bar -->
                                <div class="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5">
                                    <div class="flex items-baseline justify-between mb-3">
                                        <div class="flex items-baseline gap-2">
                                            <span class="text-[38px] font-black text-white leading-none tabular-nums">
                                                {{ audition.likes }}
                                            </span>
                                            <span class="text-zinc-500 text-sm">/ {{ audition.star_goal }} stars</span>
                                        </div>
                                        <span class="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
                                            {{ progressPercentage }}%
                                        </span>
                                    </div>

                                    <div class="relative h-3 w-full bg-zinc-800 rounded-full overflow-hidden mb-3">
                                        <div
                                            class="absolute top-0 left-0 h-full rounded-full transition-all duration-700"
                                            :style="{ width: progressPercentage + '%', background: `linear-gradient(90deg, ${theme.accent}, white)` }"
                                        />
                                    </div>

                                    <div class="flex items-center justify-between text-[12px] flex-wrap gap-2">
                                        <div class="flex items-center gap-1.5">
                                            <img :src="STAR_ICON_URL" alt="" class="w-3.5 h-3.5" />
                                            <span class="text-white font-semibold">Sign up to sponsor</span>
                                            <span class="text-zinc-500">and help decide what gets filmed</span>
                                        </div>
                                        <span class="text-zinc-300">
                                            <span class="text-white font-semibold">{{ starsNeeded }}</span>
                                            left to film
                                        </span>
                                    </div>

                                    <!-- Inline social-proof row (reuses today_sponsors_count / top_sponsor_stars) -->
                                    <div
                                        v-if="audition.today_sponsors_count > 0 || audition.top_sponsor_stars > 0"
                                        class="mt-4 pt-4 border-t border-zinc-800/70 space-y-2"
                                    >
                                        <div
                                            v-if="audition.today_sponsors_count > 0"
                                            class="flex items-center gap-2 text-xs text-zinc-300"
                                        >
                                            <div class="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                                                <Crown class="w-3 h-3 text-amber-400" />
                                            </div>
                                            <span>
                                                <strong class="text-white">{{ audition.today_sponsors_count }}</strong>
                                                sponsors today
                                            </span>
                                        </div>
                                        <div
                                            v-if="audition.top_sponsor_stars > 0"
                                            class="flex items-center gap-2 text-xs text-zinc-300"
                                        >
                                            <div class="w-5 h-5 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0">
                                                <Flame class="w-3 h-3 text-orange-400" />
                                            </div>
                                            <span>
                                                Top sponsor gave
                                                <strong class="text-white">{{ audition.top_sponsor_stars }} Stars</strong>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- ─── PITCH HERO (lifecycle ladder) ─────────────────────── -->
                            <div v-else-if="isPitchContext" class="mb-5">
                                <!-- Performer strip — only when a performer is attached -->
                                <div v-if="performer?.thumbnail_url" class="flex items-center gap-3 mb-4">
                                    <img
                                        :src="performer.thumbnail_url"
                                        :alt="performer.name"
                                        class="w-12 h-12 rounded-full object-cover object-top border-2 border-zinc-800 shrink-0"
                                    />
                                    <div class="min-w-0">
                                        <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 mb-0.5">
                                            With
                                        </p>
                                        <p class="text-white font-bold text-[15px] truncate leading-tight">
                                            {{ performer.name }}
                                        </p>
                                    </div>
                                </div>
                                <!-- Fallback iconography when no performer -->
                                <div v-else class="flex items-center gap-3 mb-4">
                                    <div class="w-12 h-12 rounded-xl bg-gradient-to-b from-zinc-800 to-zinc-950 border border-zinc-700 flex items-center justify-center shrink-0">
                                        <Clapperboard class="w-6 h-6 text-blue-400" />
                                    </div>
                                    <div class="min-w-0">
                                        <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 mb-0.5">
                                            Submit a pitch
                                        </p>
                                        <p class="text-white font-bold text-[15px] truncate leading-tight">
                                            Open call
                                        </p>
                                    </div>
                                </div>

                                <ol class="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-3.5">
                                    <li
                                        v-for="(step, i) in PITCH_STEPS"
                                        :key="i"
                                        class="relative flex items-start gap-3"
                                    >
                                        <div class="relative flex flex-col items-center shrink-0">
                                            <div
                                                class="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black border-2"
                                                :style="i === 0
                                                    ? { background: theme.accent, color: 'white', borderColor: theme.accent, boxShadow: `0 0 14px ${theme.accent}` }
                                                    : { background: 'rgb(24 24 27)', color: 'rgb(161 161 170)', borderColor: 'rgb(63 63 70)' }"
                                            >{{ i + 1 }}</div>
                                            <div
                                                v-if="i < PITCH_STEPS.length - 1"
                                                class="w-px flex-1 bg-zinc-800 mt-1"
                                                style="height: 18px;"
                                            />
                                        </div>
                                        <div class="flex-1 min-w-0 pb-1">
                                            <p
                                                class="font-bold text-[14px] leading-tight"
                                                :class="i === 0 ? 'text-white' : 'text-zinc-300'"
                                            >
                                                {{ step.label }}
                                                <span
                                                    v-if="i === 0"
                                                    class="ml-2 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold rounded uppercase tracking-wider"
                                                    :style="{ background: `${theme.accent}22`, color: theme.accent }"
                                                >You</span>
                                            </p>
                                            <p
                                                class="text-xs mt-0.5"
                                                :class="i === 0 ? 'text-zinc-300' : 'text-zinc-500'"
                                            >{{ step.desc }}</p>
                                        </div>
                                    </li>
                                </ol>
                            </div>

                            <!-- ─── GENERAL HERO ──────────────────────────────────────── -->
                            <div v-else class="mb-5">
                                <div class="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4">
                                    <div class="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                                        <div>
                                            <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400 mb-1">
                                                Producers sponsored
                                            </p>
                                            <p v-if="!isLoadingStats" class="text-[28px] font-black text-white leading-none tabular-nums">
                                                {{ releasedAuditionsCount }} scenes
                                            </p>
                                            <div v-else class="h-9 w-32 bg-zinc-800 rounded animate-pulse"></div>
                                        </div>
                                    </div>

                                    <!-- Loading skeleton -->
                                    <div v-if="isLoadingStats" class="flex gap-2">
                                        <div
                                            v-for="i in 4"
                                            :key="i"
                                            class="flex-shrink-0 aspect-[4/5] rounded-lg bg-zinc-800 animate-pulse"
                                            style="width: calc((100% - 0.5rem * 3) / 4);"
                                        ></div>
                                    </div>

                                    <!-- Carousel -->
                                    <div v-else-if="displayedPosts.length > 0" class="relative overflow-hidden">
                                        <div class="carousel-track flex gap-2">
                                            <div
                                                v-for="(post, i) in displayedPosts"
                                                :key="`${post.id}-${i}`"
                                                class="carousel-item flex-shrink-0 relative aspect-[4/5] rounded-lg overflow-hidden bg-zinc-800"
                                                style="width: calc((100% - 0.5rem * 3) / 4);"
                                            >
                                                <img :src="post.start_card_image || post.avatar" :alt="post.title" class="w-full h-full object-cover" />
                                                <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                            </div>
                                        </div>
                                    </div>
                                    <p class="text-[11px] text-zinc-500 mt-3">
                                        Each one started as a community pitch.
                                    </p>
                                </div>
                            </div>

                            <!-- ─── Email (optional) ──────────────────────────────────── -->
                            <div class="mb-3">
                                <input
                                    v-model="email"
                                    type="email"
                                    placeholder="Email (optional)"
                                    :class="[
                                        'w-full h-11 rounded-xl bg-zinc-900 border text-white text-sm placeholder-zinc-500 px-4 focus:outline-none focus:ring-1 transition-colors',
                                        emailError
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                            : 'border-zinc-800 hover:border-zinc-700 focus:border-blue-500 focus:ring-blue-500'
                                    ]"
                                    @input="emailError = ''"
                                />
                                <p v-if="emailError" class="mt-1.5 text-xs text-red-400">
                                    {{ emailError }}
                                </p>
                            </div>

                            <!-- ─── CTA ───────────────────────────────────────────────── -->
                            <button
                                type="button"
                                class="group relative overflow-hidden w-full inline-flex items-center justify-center gap-2 rounded-xl py-3.5 px-6 font-bold text-base transition-all active:scale-[0.98] shadow-lg border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-950"
                                :class="theme.ctaClass"
                                @click="handleJoin"
                            >
                                <span>{{ copy.ctaLabel }}</span>
                            </button>

                            <!-- Micro trust bullets -->
                            <ul class="flex items-center justify-center gap-3 mt-3 flex-wrap">
                                <li
                                    v-for="(b, i) in copy.microBullets"
                                    :key="i"
                                    class="flex items-center gap-1.5 text-[11px] text-zinc-500"
                                >
                                    <Check class="w-3 h-3 text-emerald-400" />
                                    <span>{{ b }}</span>
                                </li>
                            </ul>

                            <p class="text-center text-sm text-zinc-500 mt-3">
                                Already a member?
                                <a
                                    :href="route('members2.auth.login', { dest: currentUrl })"
                                    class="font-semibold transition-colors"
                                    :class="theme.linkClass"
                                >
                                    Sign in
                                </a>
                            </p>
                        </template>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.stakes-modal-enter-active,
.stakes-modal-leave-active {
    transition: opacity 0.3s ease;
}
.stakes-modal-enter-from,
.stakes-modal-leave-to {
    opacity: 0;
}

/* Infinite scroll carousel animation */
.carousel-track {
    animation: scroll-left 10s linear infinite;
}

@keyframes scroll-left {
    0% {
        transform: translateX(0);
    }
    100% {
        transform: translateX(-50%);
    }
}

/* Pause animation on hover */
.carousel-track:hover {
    animation-play-state: paused;
}
</style>
